import React, { useState, useContext, useEffect } from 'react'
import AppLayout from '../layouts/AppLayout'
import { AppContext } from '../contexts/AppContext'
import VPNMatchdayTable from '../components/VPNMatchdayTable'
import VPNPeriodTable from '../components/VPNPeriodTable'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { toast } from 'react-toastify'
import {
  FaCalendarAlt,
  FaFilter,
  FaDownload,
  FaChartBar,
  FaTable,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaEye,
  FaFilePdf,
  FaTv,
  FaShieldAlt,
} from 'react-icons/fa'
import Bounce from 'react-activity/dist/Bounce'
import 'react-activity/dist/Bounce.css'

const VPNReports = () => {
  const {
    isLoading,
    getVpnTests,
    getVpnTestStats,
    getBroadcasters,
    getCompetitions,
    getVpnMatchdayReports,
    getVpnWeeklyReports,
    getVpnMonthlyReports,
    getVpnPeriodReports,
    getVpnReportSummary,
  } = useContext(AppContext)

  // State for view toggle
  const [activeView, setActiveView] = useState('matchday') // 'matchday' or 'period'

  // Handle view change
  const handleViewChange = async (view) => {
    setActiveView(view)
    // Reset filters when changing view
    if (view === 'matchday') {
      setMatchdayFilters({
        matchday: '',
        competition: '',
        broadcaster: '',
      })
      // Load matchday data
      const tests = await getVpnMatchdayReports()
      if (tests) setVpnTests(tests)
      const summary = await getVpnReportSummary()
      if (summary) setStats(summary)
    } else {
      setPeriodFilters({
        competition: '',
        broadcaster: '',
        vpn: '',
        dateFilter: { startDate: '', endDate: '' },
      })
      // Load period data based on selected period
      if (selectedPeriod === 'week') {
        const tests = await getVpnWeeklyReports()
        if (tests) setVpnTests(tests)
      } else {
        const tests = await getVpnMonthlyReports()
        if (tests) setVpnTests(tests)
      }
      const summary = await getVpnReportSummary()
      if (summary) setStats(summary)
    }
  }

  // State for data
  const [vpnTests, setVpnTests] = useState([])
  const [stats, setStats] = useState({})
  const [broadcasters, setBroadcasters] = useState([])
  const [competitions, setCompetitions] = useState([])

  // State for filters
  const [matchdayFilters, setMatchdayFilters] = useState({
    matchday: '',
    competition: '',
    broadcaster: '',
  })

  const [periodFilters, setPeriodFilters] = useState({
    competition: '',
    broadcaster: '',
    vpn: '',
    dateFilter: { startDate: '', endDate: '' },
  })

  // State for UI
  const [selectedMatchday, setSelectedMatchday] = useState('')
  const [selectedPeriod, setSelectedPeriod] = useState('week') // 'week' or 'month'

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load competitions
        const comps = await getCompetitions()
        if (comps) setCompetitions(comps.filter((comp) => comp.name !== 'Ligue 2'))

        // Load broadcasters
        const bcasters = await getBroadcasters()
        if (bcasters) setBroadcasters(bcasters)

        // Load initial VPN tests and stats
        const tests = await getVpnMatchdayReports()
        if (tests) setVpnTests(tests)

        const initialStats = await getVpnReportSummary()
        if (initialStats) setStats(initialStats)
      } catch (error) {
        console.error('Error loading initial data:', error)
      }
    }

    loadInitialData()
  }, [
    getCompetitions,
    getBroadcasters,
    getVpnMatchdayReports,
    getVpnWeeklyReports,
    getVpnMonthlyReports,
    getVpnReportSummary,
  ])

  // Load data based on filters
  const loadFilteredData = async () => {
    try {
      if (activeView === 'matchday') {
        const tests = await getVpnMatchdayReports(matchdayFilters)
        if (tests) setVpnTests(tests)
      } else {
        // Use new separate endpoints for weekly and monthly reports
        if (selectedPeriod === 'week') {
          const tests = await getVpnWeeklyReports(periodFilters)
          if (tests) setVpnTests(tests)
        } else {
          const tests = await getVpnMonthlyReports(periodFilters)
          if (tests) setVpnTests(tests)
        }
      }

      const summary = await getVpnReportSummary(activeView === 'matchday' ? matchdayFilters : periodFilters)
      if (summary) setStats(summary)
    } catch (error) {
      console.error('Error loading filtered data:', error)
    }
  }

  // Handle filter changes
  const handleMatchdayFilterChange = (key, value) => {
    setMatchdayFilters((prev) => ({ ...prev, [key]: value }))
  }

  const handlePeriodFilterChange = (key, value) => {
    if (key === 'dateFilter') {
      setPeriodFilters((prev) => ({ ...prev, dateFilter: value }))
    } else {
      setPeriodFilters((prev) => ({ ...prev, [key]: value }))
    }
  }

  // Apply filters
  const applyFilters = () => {
    loadFilteredData()
  }

  // Reset filters
  const resetFilters = () => {
    if (activeView === 'matchday') {
      setMatchdayFilters({
        matchday: '',
        competition: '',
        broadcaster: '',
      })
    } else {
      setPeriodFilters({
        competition: '',
        broadcaster: '',
        vpn: '',
        dateFilter: { startDate: '', endDate: '' },
      })
    }
    loadFilteredData()
  }

  // Generate PDF report
  const generatePDF = async (type) => {
    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      })

      let tableData = []
      let tableHeaders = []

      if (activeView === 'matchday') {
        // Matchday table data - exact columns from the screenshot
        tableHeaders = [
          'Timestamp',
          'Result of the new test',
          'In which country are you located?',
          'Which VPN did you use?',
          'Which broadcaster did you try?',
          'For which country did you check?',
          'While the VPN was activated, were you able to successfully register with the broadcaster using a French credit or debit card?',
          'While the VPN was activated, were you able to successfully watch Ligue 1 content or see advertisements for the Ligue?',
          'While the\nVPN was\ndeactivated,\nwere you\nable to\nsuccessfully\nwatch Ligue\n1 content or\nsee\nadvertisemen\nts for the\nLigue?',
          'Please add a VPN IP address if you were able to stream successfully.',
        ]

        tableData = vpnTests.map((test) => {
          // Format timestamp to match table format
          const timestamp = test.test_date ? formatDate(test.test_date) : 'N/A'

          // Normalize test result for consistent color coding
          let testResult = test.test_result || 'Fail'
          testResult = testResult.toString().trim()

          // Ensure consistent result values for color coding
          if (testResult.toLowerCase() === 'success' || testResult.toLowerCase() === 'succeeded') {
            testResult = 'Success'
          } else if (
            testResult.toLowerCase() === 'blocked' ||
            testResult.toLowerCase() === 'failed' ||
            testResult.toLowerCase() === 'fail'
          ) {
            testResult = 'Failed'
          }

          return [
            timestamp,
            testResult,
            test.country_located || 'N/A', // Use actual country located data
            test.vpn_name || 'N/A',
            test.broadcaster_name || 'N/A',
            test.country_checked || 'N/A', // Use actual country checked data
            convertTinyintToDisplay(test.french_card_registration),
            convertTinyintToDisplay(test.ligue1_content_while_activated),
            convertTinyintToDisplay(test.ligue1_content_while_deactivated),
            test.vpn_ip_address || '',
          ]
        })
      } else {
        // Period table data
        if (selectedPeriod === 'week') {
          tableHeaders = ['Broadcaster', 'Date', 'Blocked', 'Failed', 'Success Rate']

          // Group data by period, then broadcaster
          const periodBroadcasterMap = {}
          vpnTests.forEach((row) => {
            const period = row.period
            const broadcaster = row.broadcaster_name || 'Unknown Broadcaster'
            if (!periodBroadcasterMap[period]) periodBroadcasterMap[period] = {}

            if (!periodBroadcasterMap[period][broadcaster]) {
              periodBroadcasterMap[period][broadcaster] = {
                blocked: row.blocked_tests || 0,
                failed: row.failed_tests || 0,
                success_rate: row.success_rate || 0,
              }
            }
          })

          // Flatten the data for table
          Object.entries(periodBroadcasterMap).forEach(([period, broadcasters]) => {
            Object.entries(broadcasters).forEach(([broadcaster, stats]) => {
              tableData.push([broadcaster, `Week ${period}`, stats.blocked, stats.failed, `${stats.success_rate}%`])
            })
          })
        } else {
          // Monthly view
          tableHeaders = ['Broadcaster', 'Date', 'Blocked', 'Failed', 'Success Rate']

          // Group data by period, then broadcaster
          const periodBroadcasterMap = {}
          vpnTests.forEach((row) => {
            const period = row.period
            const broadcaster = row.broadcaster_name || 'Unknown Broadcaster'
            if (!periodBroadcasterMap[period]) periodBroadcasterMap[period] = {}

            if (!periodBroadcasterMap[period][broadcaster]) {
              periodBroadcasterMap[period][broadcaster] = {
                blocked: row.blocked_tests || 0,
                failed: row.failed_tests || 0,
                success_rate: row.success_rate || 0,
              }
            }
          })

          // Flatten the data for table
          Object.entries(periodBroadcasterMap).forEach(([period, broadcasters]) => {
            Object.entries(broadcasters).forEach(([broadcaster, stats]) => {
              tableData.push([broadcaster, period, stats.blocked, stats.failed, `${stats.success_rate}%`])
            })
          })
        }
      }

      // Add table with styling to match the screenshot
      if (tableData.length > 0) {
        autoTable(doc, {
          head: [tableHeaders],
          body: tableData,
          startY: 10,
          styles: {
            fontSize: 7,
            cellPadding: 3,
            halign: 'center',
            valign: 'middle',
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
          },
          headStyles: {
            fillColor: [75, 90, 102], // Dark gray header (#4B5A66)
            textColor: [255, 255, 255], // White text
            fontStyle: 'bold',
            halign: 'center',
            valign: 'middle',
            fontSize: 7,
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252], // Light gray alternating rows
          },
          columnStyles: {
            0: { cellWidth: 25, halign: 'center' }, // Timestamp
            1: { cellWidth: 20, halign: 'center' }, // Result
            2: { cellWidth: 25, halign: 'center' }, // Country located
            3: { cellWidth: 20, halign: 'center' }, // VPN used
            4: { cellWidth: 20, halign: 'center' }, // Broadcaster
            5: { cellWidth: 20, halign: 'center' }, // Country checked
            6: { cellWidth: 35, halign: 'center' }, // French card registration
            7: { cellWidth: 35, halign: 'center' }, // VPN activated Ligue 1
            8: { cellWidth: 40, halign: 'center' }, // VPN deactivated Ligue 1 (wider for multi-line text)
            9: { cellWidth: 25, halign: 'center' }, // IP address
          },
          createdCell: function (cell, data) {
            // Color code the "Result of the new test" column (column index 1)
            if (data.column.index === 1 && data.row.index > 0) {
              const result = data.cell.text.join('').trim()
              console.log('Cell result:', result, 'for row:', data.row.index) // Debug log
              if (result === 'Success') {
                cell.styles.fillColor = [34, 197, 94] // Green for Success
                cell.styles.textColor = [255, 255, 255] // White text
              } else if (result === 'Failed') {
                cell.styles.fillColor = [239, 68, 68] // Red for Failed
                cell.styles.textColor = [255, 255, 255] // White text
              }
            }
          },
          didParseCell: function (data) {
            // Alternative approach for cell coloring
            if (data.column.index === 1) {
              const result = data.cell.text.join('').trim()
              if (result === 'Success') {
                data.cell.styles.fillColor = [34, 197, 94] // Green for Success
                data.cell.styles.textColor = [255, 255, 255] // White text
              } else if (result === 'Failed') {
                data.cell.styles.fillColor = [239, 68, 68] // Red for Failed
                data.cell.styles.textColor = [255, 255, 255] // White text
              }
            }
          },
        })
      }

      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0]
      const filename = `vpn-reports-matchday-week-${timestamp}.pdf`

      // Save the PDF
      doc.save(filename)

      toast.success('PDF report generated successfully!')
    } catch (error) {
      console.error('Error generating PDF:', error)
      toast.error('Failed to generate PDF report')
    }
  }

  // Helper function to safely format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'N/A'

      // Format with date, hours, minutes, and seconds
      const dateStr = date.toLocaleDateString()
      const timeStr = date.toLocaleTimeString('en-US', {
        hour12: true,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
      return `${dateStr} ${timeStr}`
    } catch (error) {
      return 'N/A'
    }
  }

  // Helper function to convert tinyint values to display strings
  const convertTinyintToDisplay = (value) => {
    if (value === 1) return 'Yes'
    if (value === 0) return 'No'
    return 'N/A'
  }

  return (
    <AppLayout pageName='VPN Reports'>
      <div className='container mx-auto px-4 py-6'>
        <div className='w-full mx-auto'>
          {/* Header */}
          <div className='text-center mb-8'>
            <div className='inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4'>
              <FaFilePdf className='w-8 h-8 text-blue-600' />
            </div>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>VPN Reports</h1>
            <p className='text-gray-600'>Monitor VPN effectiveness and access trends across different platforms</p>
          </div>

          {/* View Toggle */}
          <div className='bg-white rounded-lg shadow-lg p-6 mb-6'>
            <div className='flex items-center justify-between mb-6'>
              <div className='flex items-center'>
                <FaChartBar className='w-6 h-6 text-indigo-600 mr-3' />
                <h2 className='text-2xl font-bold text-gray-900'>Report Views</h2>
              </div>
            </div>

            <div className='flex justify-center mb-4'>
              <div className='flex bg-gray-100 rounded-lg p-1'>
                <button
                  type='button'
                  className={`px-6 py-2 rounded-md font-medium transition-colors ${
                    activeView === 'matchday'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => handleViewChange('matchday')}
                >
                  <FaCalendarAlt className='mr-2 inline' />
                  By Matchday
                </button>
                <button
                  type='button'
                  className={`px-6 py-2 rounded-md font-medium transition-colors ${
                    activeView === 'period' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => handleViewChange('period')}
                >
                  <FaChartBar className='mr-2 inline' />
                  By {selectedPeriod === 'week' ? 'Week' : 'Month'}
                </button>
              </div>
            </div>

            {activeView === 'period' && (
              <div className='flex justify-center'>
                <div className='flex bg-gray-100 rounded-lg p-1'>
                  <button
                    type='button'
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedPeriod === 'week'
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    onClick={async () => {
                      setSelectedPeriod('week')
                      setVpnTests([])
                      setStats({})
                      const tests = await getVpnWeeklyReports(periodFilters)
                      if (tests) setVpnTests(tests)
                      const summary = await getVpnReportSummary(periodFilters)
                      if (summary) setStats(summary)
                    }}
                  >
                    Weekly
                  </button>
                  <button
                    type='button'
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedPeriod === 'month'
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    onClick={async () => {
                      setSelectedPeriod('month')
                      setVpnTests([])
                      setStats({})
                      const tests = await getVpnMonthlyReports(periodFilters)
                      if (tests) setVpnTests(tests)
                      const summary = await getVpnReportSummary(periodFilters)
                      if (summary) setStats(summary)
                    }}
                  >
                    Monthly
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className='bg-white rounded-lg shadow-lg p-6 mb-6'>
            <div className='flex items-center mb-4'>
              <FaFilter className='w-5 h-5 text-gray-600 mr-2' />
              <h3 className='text-lg font-semibold text-gray-900'>Filters</h3>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-4'>
              {activeView === 'matchday' ? (
                <>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Matchday</label>
                    <input
                      type='text'
                      className='form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2 w-full'
                      value={matchdayFilters.matchday}
                      onChange={(e) => handleMatchdayFilterChange('matchday', e.target.value)}
                      placeholder='Enter matchday'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Broadcaster</label>
                    <select
                      className='form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2 w-full'
                      value={matchdayFilters.broadcaster}
                      onChange={(e) => handleMatchdayFilterChange('broadcaster', e.target.value)}
                    >
                      <option value=''>All Broadcasters</option>
                      {broadcasters.map((b) => (
                        <option
                          key={b.id}
                          value={b.name}
                        >
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Start Date</label>
                    <input
                      type='date'
                      className='form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2 w-full'
                      value={periodFilters.dateFilter.startDate}
                      onChange={(e) =>
                        handlePeriodFilterChange('dateFilter', {
                          ...periodFilters.dateFilter,
                          startDate: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>End Date</label>
                    <input
                      type='date'
                      className='form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2 w-full'
                      value={periodFilters.dateFilter.endDate}
                      onChange={(e) =>
                        handlePeriodFilterChange('dateFilter', {
                          ...periodFilters.dateFilter,
                          endDate: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Broadcaster</label>
                    <select
                      className='form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2 w-full'
                      value={periodFilters.broadcaster}
                      onChange={(e) => handlePeriodFilterChange('broadcaster', e.target.value)}
                    >
                      <option value=''>All Broadcasters</option>
                      {broadcasters.map((b) => (
                        <option
                          key={b.id}
                          value={b.name}
                        >
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
            <div className='flex gap-2'>
              <button
                className='btn-submit bg-gray-600 hover:bg-gray-700 border-gray-600'
                onClick={applyFilters}
                disabled={isLoading}
              >
                {isLoading ? <Bounce /> : <FaFilter />}
                {isLoading ? ' Loading...' : ' Apply Filters'}
              </button>
              <button
                className='btn-secondary'
                onClick={resetFilters}
              >
                Reset
              </button>
            </div>
          </div>

          {/* Stats Summary */}
          <div className='bg-white rounded-lg shadow-lg p-6 mb-6'>
            <div className='flex items-center justify-between mb-6'>
              <div className='flex items-center'>
                <FaChartBar className='w-6 h-6 text-indigo-600 mr-3' />
                <h2 className='text-2xl font-bold text-gray-900'>Summary Statistics</h2>
              </div>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
              <div className='bg-blue-50 rounded-lg p-4'>
                <div className='flex items-center'>
                  <FaChartBar className='w-5 h-5 text-blue-600 mr-2' />
                  <div>
                    <p className='text-sm text-blue-600'>Total Tests</p>
                    <p className='text-2xl font-bold text-blue-900'>{stats.total_tests || 0}</p>
                  </div>
                </div>
              </div>
              <div className='bg-green-50 rounded-lg p-4'>
                <div className='flex items-center'>
                  <FaCheck className='w-5 h-5 text-green-600 mr-2' />
                  <div>
                    <p className='text-sm text-green-600'>Successful</p>
                    <p className='text-2xl font-bold text-green-900'>{stats.successful_tests || 0}</p>
                  </div>
                </div>
              </div>
              <div className='bg-red-50 rounded-lg p-4'>
                <div className='flex items-center'>
                  <FaTimes className='w-5 h-5 text-red-600 mr-2' />
                  <div>
                    <p className='text-sm text-red-600'>Blocked</p>
                    <p className='text-2xl font-bold text-red-900'>{stats.blocked_tests || 0}</p>
                  </div>
                </div>
              </div>
              <div className='bg-yellow-50 rounded-lg p-4'>
                <div className='flex items-center'>
                  <FaExclamationTriangle className='w-5 h-5 text-yellow-600 mr-2' />
                  <div>
                    <p className='text-sm text-yellow-600'>Failed</p>
                    <p className='text-2xl font-bold text-yellow-900'>{stats.failed_tests || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div className='bg-purple-50 rounded-lg p-4'>
                <div className='flex items-center'>
                  <FaTv className='w-5 h-5 text-purple-600 mr-2' />
                  <div>
                    <p className='text-sm text-purple-600'>Broadcasters</p>
                    <p className='text-2xl font-bold text-purple-900'>{stats.unique_broadcasters || 0}</p>
                  </div>
                </div>
              </div>
              <div className='bg-indigo-50 rounded-lg p-4'>
                <div className='flex items-center'>
                  <FaShieldAlt className='w-5 h-5 text-indigo-600 mr-2' />
                  <div>
                    <p className='text-sm text-indigo-600'>VPNs</p>
                    <p className='text-2xl font-bold text-indigo-900'>{stats.unique_vpns || 0}</p>
                  </div>
                </div>
              </div>
              <div className='bg-cyan-50 rounded-lg p-4'>
                <div className='flex items-center'>
                  <FaChartBar className='w-5 h-5 text-cyan-600 mr-2' />
                  <div>
                    <p className='text-sm text-cyan-600'>Success Rate</p>
                    <p className='text-2xl font-bold text-cyan-900'>{stats.success_rate || 0}%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className='bg-white rounded-lg shadow-lg p-6'>
            <div className='flex items-center justify-between mb-6'>
              <div className='flex items-center'>
                <FaTable className='w-6 h-6 text-indigo-600 mr-3' />
                <h2 className='text-2xl font-bold text-gray-900'>
                  {activeView === 'matchday'
                    ? 'Matchday Results'
                    : `${selectedPeriod === 'week' ? 'Weekly' : 'Monthly'} Results`}
                </h2>
              </div>

              {/* Action Buttons */}
              <div className='flex justify-end mb-6'>
                <button
                  className='btn-submit bg-blue-600 hover:bg-blue-700 border-blue-600'
                  onClick={() => generatePDF(activeView)}
                >
                  <FaFilePdf className='mr-2' />
                  Download PDF
                </button>
              </div>
            </div>

            {activeView === 'matchday' ? (
              <VPNMatchdayTable
                vpnTests={vpnTests}
                formatDate={formatDate}
              />
            ) : activeView === 'period' ? (
              <VPNPeriodTable
                vpnTests={vpnTests}
                selectedPeriod={selectedPeriod}
                formatDate={formatDate}
              />
            ) : null}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

export default VPNReports
